import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Activity, ArrowLeft, ArrowRight, Check, Building2, Mail, User,
  Sparkles, Network, Power, Zap, Server, Radio,
} from "lucide-react";
import { useAuth, type DaemonProfile } from "@/lib/auth-context";
import { PRODUCT_HOME, PRODUCT_NAME } from "@/lib/product";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/onboarding")({
  head: () => ({ meta: [{ title: `Set up workspace · ${PRODUCT_NAME}` }] }),
  component: OnboardingPage,
});

const STEPS = [
  { key: "profile",  label: "Profile",     icon: User },
  { key: "entity",   label: "Entity type", icon: Network },
  { key: "runtime",  label: "Runtime",     icon: Power },
  { key: "review",   label: "Review",      icon: Check },
] as const;

function OnboardingPage() {
  const { user, completeOnboarding } = useAuth();
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);

  const [profile, setProfile] = useState<DaemonProfile>(() => ({
    company: "",
    userName: user?.name ?? "",
    email: user?.email ?? "",
    companyType: "hybrid",
    entityType: "hybrid",
    keepOnPerpetually: true,
  }));

  const update = <K extends keyof DaemonProfile>(k: K, v: DaemonProfile[K]) =>
    setProfile((p) => ({ ...p, [k]: v }));

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;
  const canBack = stepIdx > 0;

  const valid = useMemo(() => {
    switch (step.key) {
      case "profile": return profile.company.trim().length > 0 && profile.userName.trim().length > 0 && profile.email.trim().length > 0;
      case "entity":  return !!profile.entityType;
      case "runtime": return true;
      case "review":  return true;
    }
  }, [step.key, profile]);

  const handleNext = () => {
    if (!valid) return;
    if (isLast) {
      completeOnboarding(profile);
      navigate({ to: PRODUCT_HOME });
      return;
    }
    setStepIdx((i) => i + 1);
  };

  return (
    <div className="genrl-route-canvas relative min-h-screen w-full overflow-hidden">
      <div className="mx-auto flex min-h-screen max-w-4xl flex-col px-4 py-10">
        {/* Header */}
        <div className="mb-8 flex items-center gap-3">
          <div
            className="relative flex h-10 w-10 items-center justify-center rounded-xl shadow-[var(--shadow-glow)]"
            style={{ background: "var(--gradient-primary)" }}
          >
            <Activity className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <p className="font-display text-base font-semibold tracking-tight">Set up wallet workspace</p>
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
              Step {stepIdx + 1} of {STEPS.length} · {step.label}
            </p>
          </div>
        </div>

        {/* Stepper */}
        <ol className="mb-8 flex items-center gap-2 overflow-x-auto pb-1">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = i < stepIdx;
            const active = i === stepIdx;
            return (
              <li key={s.key} className="flex items-center gap-2">
                <div
                  className={cn(
                    "flex h-8 items-center gap-2 rounded-full border hairline px-3 text-xs font-medium transition",
                    active && "bg-[var(--soft-fill)] text-foreground",
                    done && "text-foreground",
                    !active && !done && "text-muted-foreground",
                  )}
                  style={active ? { borderColor: "var(--ring)" } : undefined}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span>{s.label}</span>
                </div>
                {i < STEPS.length - 1 && (
                  <span className={cn("h-px w-4", done ? "bg-foreground/40" : "bg-[var(--hairline)]")} />
                )}
              </li>
            );
          })}
        </ol>

        {/* Step body */}
        <div className="glass-strong flex-1 rounded-2xl p-7">
          {step.key === "profile" && (
            <Section title="Tell us about you" hint="Company and operator identity. You can edit these later in Settings.">
              <div className="grid gap-4 md:grid-cols-2">
                <Labeled label="Company name" icon={Building2}>
                  <TextInput value={profile.company} onChange={(v) => update("company", v)} placeholder="Acme Compute Ltd" />
                </Labeled>
                <Labeled label="User name" icon={User}>
                  <TextInput value={profile.userName} onChange={(v) => update("userName", v)} placeholder="Nova Operator" />
                </Labeled>
                <Labeled label="Email" icon={Mail}>
                  <TextInput value={profile.email} onChange={(v) => update("email", v)} placeholder="ops@your.org" />
                </Labeled>
                <Labeled label="Company type" icon={Sparkles}>
                  <div className="grid grid-cols-3 gap-1.5 rounded-xl border hairline bg-soft p-1">
                    {(["cloud","enterprise","hybrid"] as const).map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => update("companyType", t)}
                        className={cn(
                          "rounded-lg py-2 text-xs font-medium capitalize transition",
                          profile.companyType === t ? "bg-background text-foreground shadow" : "text-muted-foreground hover:text-foreground",
                        )}
                      >{t}</button>
                    ))}
                  </div>
                </Labeled>
              </div>
            </Section>
          )}

          {step.key === "entity" && (
            <Section title="What kind of entity is this workspace?" hint="Advanced local runtime shape for the wallet workspace.">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {([
                  { v: "hub",    label: "Hub",    desc: "Coordinates work and routes jobs to spokes.", icon: Server },
                  { v: "spoke",  label: "Spoke",  desc: "Executes jobs on behalf of a hub.",           icon: Radio },
                  { v: "hybrid", label: "Hybrid", desc: "Acts as both — routes and executes.",         icon: Zap },
                ] as const).map((opt) => {
                  const Icon = opt.icon;
                  const active = profile.entityType === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => update("entityType", opt.v)}
                      className={cn(
                        "flex flex-col items-start gap-2 rounded-xl border hairline bg-soft p-4 text-left transition",
                        active ? "text-foreground" : "text-muted-foreground hover:text-foreground",
                      )}
                      style={active ? { borderColor: "var(--ring)" } : undefined}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-sm font-semibold">{opt.label}</span>
                      <span className="text-[11px] text-muted-foreground">{opt.desc}</span>
                    </button>
                  );
                })}
              </div>
            </Section>
          )}

          {step.key === "runtime" && (
            <Section title="Runtime mode" hint="Choose how your local wallet workspace stays available.">
              <button
                type="button"
                onClick={() => update("keepOnPerpetually", !profile.keepOnPerpetually)}
                className="flex w-full items-center justify-between rounded-xl border hairline bg-soft p-4 text-left"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-lg border hairline bg-background">
                    <Power className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">Keep local workspace on perpetually</p>
                    <p className="mt-0.5 text-[11px] text-muted-foreground">
                      Stay online 24/7 to maximise job flow and earnings. Disable to run only when manually started.
                    </p>
                  </div>
                </div>
                <span
                  className="relative h-6 w-11 shrink-0 rounded-full transition"
                  style={{ background: profile.keepOnPerpetually ? "var(--gradient-primary)" : "var(--soft-fill)" }}
                >
                  <span
                    className={cn(
                      "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all",
                      profile.keepOnPerpetually ? "left-[22px]" : "left-0.5",
                    )}
                  />
                </span>
              </button>
            </Section>
          )}

          {step.key === "review" && (
            <Section title="Review & launch" hint="Confirm your wallet workspace profile. You can edit any of these later in settings.">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <ReviewRow icon={<Building2 className="h-3.5 w-3.5" />} label="Company"      value={profile.company || "—"} />
                <ReviewRow icon={<User className="h-3.5 w-3.5" />}      label="User"         value={profile.userName || "—"} />
                <ReviewRow icon={<Mail className="h-3.5 w-3.5" />}      label="Email"        value={profile.email || "—"} />
                <ReviewRow icon={<Sparkles className="h-3.5 w-3.5" />}  label="Company type" value={profile.companyType} />
                <ReviewRow icon={<Network className="h-3.5 w-3.5" />}   label="Entity type"  value={profile.entityType} />
                <ReviewRow icon={<Power className="h-3.5 w-3.5" />}     label="Keep-on"      value={profile.keepOnPerpetually ? "Perpetual" : "On-demand"} />
              </div>
            </Section>
          )}

          {/* Footer nav */}
          <div className="mt-8 flex items-center justify-between border-t hairline pt-5">
            <button
              type="button"
              disabled={!canBack}
              onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
              className={cn(
                "flex h-10 items-center gap-1.5 rounded-xl border hairline px-4 text-sm font-medium transition",
                canBack ? "text-foreground hover:bg-soft" : "cursor-not-allowed text-muted-foreground/60",
              )}
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <button
              type="button"
              disabled={!valid}
              onClick={handleNext}
              className={cn(
                "flex h-10 items-center gap-1.5 rounded-xl px-5 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition",
                valid ? "hover:opacity-95" : "cursor-not-allowed opacity-50",
              )}
              style={{ background: "var(--gradient-primary)" }}
            >
              {isLast ? "Launch daemon" : "Continue"} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- small primitives ---------- */

function Section({
  title, hint, children,
}: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl font-semibold tracking-tight">{title}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{hint}</p>
      </div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Labeled({
  label, icon: Icon, children,
}: { label: string; icon?: React.ComponentType<{ className?: string }>; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </p>
      {children}
    </div>
  );
}

function TextInput({
  value, onChange, placeholder,
}: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-11 w-full rounded-xl border hairline bg-soft px-3.5 text-sm outline-none transition focus:border-[var(--ring)] placeholder:text-muted-foreground/70"
    />
  );
}

function ReviewRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-xl border hairline bg-soft px-3.5 py-2.5">
      <span className="flex items-center gap-2 text-xs text-muted-foreground">
        {icon}
        {label}
      </span>
      <span className="text-sm font-medium capitalize">{value}</span>
    </div>
  );
}
